import { ParsedAnswer } from '../../Teleexpertise/questionTypes/_props'
import { validateNumber } from '../../utils/number'
import { validateDateInRange } from '../../utils/date'

export type SkipDisplay = {
    showSkip: boolean
    skipReplacesNext: boolean
}

/**
 * Navigation helpers for workflow questions.
 * - canGoNext: validates if current answer allows progression
 * - showSkip: controls visibility of Skip button for optional questions
 * Each input type (text, number, date, slider, etc.) has specific validation logic.
 */

/**
 * skip button display rules for optional questions:
 * - single choice: always shown instead of Next (the selection moves to the next question)
 * - slider: always shown alongside Next (the slider value cannot be reset)
 * - other types: shown instead of Next while the question has no answer
 */
export const getSkipDisplay = (
    currentQuestion: any,
    isChoiceQuestion: boolean,
    isMultipleChoiceQuestion: boolean,
    localAnswersByQuestionId: Map<string, any[]>,
): SkipDisplay => {
    const hidden = { showSkip: false, skipReplacesNext: false }
    if (!currentQuestion?.optional) return hidden
    if (currentQuestion?.inputType === 'calculated') return hidden
    if (currentQuestion?.inputType === 'slider') return { showSkip: true, skipReplacesNext: false }
    if (isChoiceQuestion && !isMultipleChoiceQuestion) return { showSkip: true, skipReplacesNext: true }

    const localAnswers = localAnswersByQuestionId.get(currentQuestion?.id ?? '') ?? []
    const isEmpty = localAnswers.every(
        (answer) => answer == null || (typeof answer === 'string' && answer.trim().length === 0),
    )
    return isEmpty ? { showSkip: true, skipReplacesNext: true } : hidden
}

export const getCanGoNext = (
    currentQuestion: any,
    isTextFreeQuestion: boolean,
    isNumberFreeQuestion: boolean,
    isMultipleChoiceQuestion: boolean,
    requestAnswers: ParsedAnswer[],
    localAnswersByQuestionId: Map<string, any[]>,
): boolean => {
    if (currentQuestion?.inputType === 'calculated') return true
    if (currentQuestion?.inputType === 'slider') {
        return canGoNextForSlider(currentQuestion, localAnswersByQuestionId)
    }
    if (currentQuestion?.inputType === 'sketch') {
        return canGoNextForSketch(currentQuestion, requestAnswers, localAnswersByQuestionId)
    }
    if (isTextFreeQuestion) {
        return canGoNextForText(currentQuestion, requestAnswers, localAnswersByQuestionId)
    }
    if (isNumberFreeQuestion) {
        return canGoNextForNumber(currentQuestion, requestAnswers, localAnswersByQuestionId)
    }
    if (currentQuestion?.inputType === 'date') {
        return canGoNextForDate(currentQuestion, requestAnswers, localAnswersByQuestionId)
    }
    return canGoNextForChoice(currentQuestion, isMultipleChoiceQuestion, requestAnswers, localAnswersByQuestionId)
}

const canGoNextForSlider = (currentQuestion: any, localAnswersByQuestionId: Map<string, any[]>): boolean => {
    const qid = currentQuestion?.id ?? ''
    const sliderMin = Number(currentQuestion?.sliderMin)
    const localRaw = localAnswersByQuestionId.get(qid)?.[0] as number | undefined

    if (localRaw == null || localRaw === '') return true
    return Number(localRaw) >= sliderMin
}

const canGoNextForText = (
    currentQuestion: any,
    requestAnswers: ParsedAnswer[],
    localAnswersByQuestionId: Map<string, any[]>,
): boolean => {
    const localText = (localAnswersByQuestionId.get(currentQuestion?.id ?? '')?.[0] ?? '') as string
    const safeRequestAnswers = Array.isArray(requestAnswers) ? requestAnswers : []
    const hasSavedAnswer = safeRequestAnswers.some(
        (r) => r.id === currentQuestion?.id && typeof r.value === 'string' && r.value.trim().length > 0,
    )
    return (!!localText && localText.trim().length > 0) || hasSavedAnswer
}

const canGoNextForSketch = (
    currentQuestion: any,
    requestAnswers: ParsedAnswer[],
    localAnswersByQuestionId: Map<string, any[]>,
): boolean => {
    const qid = currentQuestion?.id ?? ''
    const localRaw = (localAnswersByQuestionId.get(qid)?.[0] ?? '') as string
    const localIsEmpty = !localRaw || String(localRaw).trim().length === 0
    const hasSavedAnswer = requestAnswers?.some((r) => r.id === qid && r.value != null && String(r.value).trim() !== '')

    if (currentQuestion?.required === false) {
        return !localIsEmpty || hasSavedAnswer
    }
    return !localIsEmpty || hasSavedAnswer
}

const canGoNextForNumber = (
    currentQuestion: any,
    requestAnswers: ParsedAnswer[],
    localAnswersByQuestionId: Map<string, any[]>,
): boolean => {
    const localRaw = (localAnswersByQuestionId.get(currentQuestion?.id ?? '')?.[0] ?? '') as string
    const expectedFormat = currentQuestion?.numberExpectedFormat
    const min = currentQuestion?.numberMin
    const max = currentQuestion?.numberMax
    const isLocalValid = validateNumber(localRaw, expectedFormat as any, min, max).isValid
    const safeRequestAnswers = Array.isArray(requestAnswers) ? requestAnswers : []
    const hasSavedAnswer = safeRequestAnswers.some(
        (r) =>
            r.id === currentQuestion?.id &&
            validateNumber(String(r.value ?? ''), expectedFormat as any, min, max).isValid,
    )
    return isLocalValid || hasSavedAnswer
}

const canGoNextForDate = (
    currentQuestion: any,
    requestAnswers: ParsedAnswer[],
    localAnswersByQuestionId: Map<string, any[]>,
): boolean => {
    const qid = currentQuestion?.id ?? ''
    const localRaw = (localAnswersByQuestionId.get(qid)?.[0] ?? '') as string
    const isLocalValid = validateDateInRange(localRaw, currentQuestion?.dateMin, currentQuestion?.dateMax).isValid
    const safeRequestAnswers = Array.isArray(requestAnswers) ? requestAnswers : []
    const hasSavedAnswer = safeRequestAnswers.some(
        (r) =>
            r.id === qid &&
            validateDateInRange(String(r.value ?? ''), currentQuestion?.dateMin, currentQuestion?.dateMax).isValid,
    )
    if (currentQuestion?.required === false) {
        if (!localRaw || localRaw.trim().length === 0) return true
        return isLocalValid || hasSavedAnswer
    }
    return isLocalValid || hasSavedAnswer
}

const canGoNextForChoice = (
    currentQuestion: any,
    isMultipleChoiceQuestion: boolean,
    requestAnswers: ParsedAnswer[],
    localAnswersByQuestionId: Map<string, any[]>,
): boolean => {
    const safeLocalAnswersByQuestionId = localAnswersByQuestionId ?? new Map<string, any[]>()
    const localAnswers = safeLocalAnswersByQuestionId.get(currentQuestion?.id ?? '')
    const hasSavedAnswer = Array.isArray(requestAnswers)
        ? requestAnswers.some((r) => r.id === currentQuestion?.id && r.value != null)
        : false
    const hasAnyAnswer = localAnswers ? localAnswers.length > 0 : hasSavedAnswer

    if (currentQuestion?.required === true) return hasAnyAnswer
    if (isMultipleChoiceQuestion) return hasAnyAnswer
    return true
}
